import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Compass,
  Layers,
  MessageCircleQuestion,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import {
  LEVEL_AWARE_STEPS,
  LEVEL_SHIFT_MARKERS,
  LOGICAL_LEVELS,
  REANCHOR_SCRIPTS,
  canAdvance,
  createDefaultLevelAwareState,
  levelLabel,
  nextStep,
  prevStep,
  stepIndex,
  suggestCoachMove,
  suggestMarkers,
  type LevelAwareState,
  type LevelAwareStep,
  type LevelShiftMarker,
  type LogicalLevel,
} from '../lib/levelAwareThinking';

const STORAGE_KEY = 'lab_level_aware_thinking_v01';

function loadState(): LevelAwareState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createDefaultLevelAwareState();
    const parsed = JSON.parse(raw) as Partial<LevelAwareState>;
    return {
      ...createDefaultLevelAwareState(),
      ...parsed,
      scenario: { ...createDefaultLevelAwareState().scenario, ...parsed.scenario },
    };
  } catch {
    return createDefaultLevelAwareState();
  }
}

function saveState(state: LevelAwareState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface LevelAwareThinkingProps {
  embedded?: boolean;
  onComplete?: (state: LevelAwareState) => void;
}

export const LevelAwareThinking: React.FC<LevelAwareThinkingProps> = ({
  embedded = false,
  onComplete,
}) => {
  const [step, setStep] = useState<LevelAwareStep>('intro');
  const [state, setState] = useState<LevelAwareState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const progressPct = useMemo(
    () => ((stepIndex(step) + 1) / LEVEL_AWARE_STEPS.length) * 100,
    [step],
  );

  const updateScenario = useCallback(
    (updates: Partial<LevelAwareState['scenario']>) => {
      setState(prev => ({
        ...prev,
        scenario: { ...prev.scenario, ...updates },
      }));
    },
    [],
  );

  const toggleMarker = useCallback((marker: LevelShiftMarker) => {
    setState(prev => {
      const current = prev.scenario.markers;
      const next = current.includes(marker)
        ? current.filter(m => m !== marker)
        : [...current, marker];
      return { ...prev, scenario: { ...prev.scenario, markers: next } };
    });
  }, []);

  const goNext = useCallback(() => {
    if (!canAdvance(step, state)) return;

    if (step === 'capture') {
      const suggested = suggestMarkers(state.scenario);
      const coachMove = suggestCoachMove(state.scenario);
      setState(prev => ({
        ...prev,
        scenario: {
          ...prev.scenario,
          markers: prev.scenario.markers.length ? prev.scenario.markers : suggested,
          coachMove: prev.scenario.coachMove || coachMove,
        },
      }));
    }

    if (step === 'reanchor') {
      const completed = { ...state, completedAt: new Date().toISOString() };
      setState(completed);
      onComplete?.(completed);
    }

    const n = nextStep(step);
    if (n) setStep(n);
  }, [step, state, onComplete]);

  const goBack = useCallback(() => {
    const p = prevStep(step);
    if (p) setStep(p);
  }, [step]);

  const reset = useCallback(() => {
    setState(createDefaultLevelAwareState());
    setStep('intro');
  }, []);

  const inputClass = embedded
    ? 'w-full bg-surface-elevated border border-surface-border  px-3 py-2.5 font-sans text-sm text-text-secondary placeholder-text-dim focus:outline-none focus:border-[var(--green)]/40 resize-none'
    : 'w-full bg-[var(--bg-raised)] border border-charcoal-200  px-3 py-2.5 text-sm text-charcoal-800 placeholder-charcoal-400 focus:outline-none focus:border-consulting-slate-blue-400 resize-none';

  const selectClass = embedded
    ? 'w-full bg-surface-elevated border border-surface-border  px-3 py-2 font-sans text-sm text-text-secondary focus:outline-none focus:border-[var(--green)]/40'
    : 'w-full bg-[var(--bg-raised)] border border-charcoal-200  px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:border-consulting-slate-blue-400';

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-8'}>
      {!embedded && (
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Compass className="h-8 w-8 text-consulting-slate-blue-600" />
            <h1 className="text-3xl font-heading font-bold text-charcoal-900">
              Level-Aware Thinking
            </h1>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
              v0.1
            </span>
          </div>
          <p className="text-charcoal-600 text-sm max-w-2xl mx-auto">
            Notice when you — or someone you&apos;re coaching — answers from the wrong logical level
            without realizing it. Restore coherence using Robert Dilts&apos; level stack.
          </p>
        </div>
      )}

      {embedded && (
        <div className="mb-2">
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-widest mb-2">
            Conversation coherence · v0.1
          </div>
          <h2 className="font-serif text-2xl text-text-primary font-light">
            Unintended Level Shift
          </h2>
          <p className="font-sans text-text-secondary text-sm mt-2 max-w-2xl">
            When &ldquo;serve who?&rdquo; becomes identity philosophy — both people feel the fog.
            Name the shift, return to the question&apos;s level, then rebuild.
          </p>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-3xl mx-auto w-full'}>
        <div className="flex items-center justify-between mb-2">
          <span
            className={`font-mono text-[10px] uppercase tracking-widest ${
              embedded ? 'text-text-dim' : 'text-charcoal-500'
            }`}
          >
            Step {stepIndex(step) + 1} of {LEVEL_AWARE_STEPS.length} —{' '}
            {LEVEL_AWARE_STEPS[stepIndex(step)]?.label}
          </span>
          <span className={`font-mono text-[10px] ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            {Math.round(progressPct)}%
          </span>
        </div>
        <div
          className={`h-1.5 rounded-full overflow-hidden ${
            embedded ? 'bg-surface-elevated' : 'bg-charcoal-100'
          }`}
        >
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              embedded ? 'bg-[var(--green)]' : 'bg-consulting-slate-blue-600'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div
        className={` border p-6 md:p-8 ${
          embedded
            ? 'bg-surface-card border-surface-border'
            : 'max-w-3xl mx-auto bg-[var(--bg-raised)] border-charcoal-200 shadow-sm'
        }`}
      >
        {step === 'intro' && <IntroStep embedded={embedded} />}
        {step === 'pattern' && <PatternStep embedded={embedded} />}
        {step === 'capture' && (
          <CaptureStep
            embedded={embedded}
            scenario={state.scenario}
            onChange={updateScenario}
            inputClass={inputClass}
            selectClass={selectClass}
          />
        )}
        {step === 'diagnose' && (
          <DiagnoseStep
            embedded={embedded}
            scenario={state.scenario}
            onToggleMarker={toggleMarker}
            onChange={updateScenario}
            inputClass={inputClass}
          />
        )}
        {step === 'reanchor' && (
          <ReanchorStep
            embedded={embedded}
            scenario={state.scenario}
            onChange={updateScenario}
            inputClass={inputClass}
          />
        )}
        {step === 'results' && <ResultsStep embedded={embedded} state={state} />}
      </div>

      <div
        className={`flex items-center justify-between gap-4 ${
          embedded ? '' : 'max-w-3xl mx-auto'
        }`}
      >
        <button
          type="button"
          onClick={goBack}
          disabled={step === 'intro'}
          className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            embedded
              ? 'border-surface-border text-text-secondary hover:bg-surface-elevated'
              : 'border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={reset}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
              embedded ? 'text-text-dim hover:text-text-secondary' : 'text-charcoal-500 hover:text-charcoal-700'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>

          {step !== 'results' ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance(step, state)}
              className={`inline-flex items-center gap-2 px-5 py-2  text-sm font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                embedded ? 'bg-[var(--green)] hover:bg-[var(--green)]' : 'bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700'
              }`}
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep('intro')}
              className={`inline-flex items-center gap-2 px-5 py-2  text-sm font-medium text-white ${
                embedded ? 'bg-[var(--green)] hover:bg-[var(--green)]' : 'bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700'
              }`}
            >
              Practice again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function IntroStep({ embedded }: { embedded: boolean }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MessageCircleQuestion
          className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`}
        />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          When clarity breaks down mid-conversation
        </h2>
      </div>

      <div
        className={` border p-4 ${
          embedded ? 'border-surface-border bg-surface-elevated/40' : 'border-charcoal-100 bg-[var(--surface)]'
        }`}
      >
        <p className={`text-sm italic mb-2 ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
          &ldquo;Show up with your gifts and serve.&rdquo;
        </p>
        <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
          You ask: <strong>&ldquo;Serve who?&rdquo;</strong> — a behavior-level question about
          stakeholders. They answer from identity, values, and philosophy — without noticing they
          changed altitude. Both of you feel the fog.
        </p>
      </div>

      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        This isn&apos;t semantic confusion about one word. It&apos;s a structural breakdown: the
        person switched logical levels unconsciously and answered a different question than the one
        you asked. Dilts&apos; stack gives you a repeatable way to name it and restore traction.
      </p>
    </div>
  );
}

function PatternStep({ embedded }: { embedded: boolean }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Layers className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Three markers of unintended level shift
        </h2>
      </div>

      <div className="space-y-4">
        {LEVEL_SHIFT_MARKERS.map(marker => (
          <div
            key={marker.id}
            className={` border p-4 ${
              embedded ? 'border-surface-border' : 'border-charcoal-100'
            }`}
          >
            <h3 className={`text-sm font-semibold mb-1 ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
              {marker.label}
            </h3>
            <p className={`text-sm mb-2 ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
              {marker.description}
            </p>
            <p className={`text-xs italic ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
              Signal: {marker.signal}
            </p>
          </div>
        ))}
      </div>

      <p className={`text-sm ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
        Next you&apos;ll capture a real moment — question asked, answer given — and map both to their
        logical levels.
      </p>
    </div>
  );
}

function CaptureStep({
  embedded,
  scenario,
  onChange,
  inputClass,
  selectClass,
}: {
  embedded: boolean;
  scenario: LevelAwareState['scenario'];
  onChange: (updates: Partial<LevelAwareState['scenario']>) => void;
  inputClass: string;
  selectClass: string;
}) {
  return (
    <div className="space-y-5">
      <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
        Capture the moment
      </h2>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          What question was asked? (exact words if you can)
        </span>
        <textarea
          value={scenario.questionAsked}
          onChange={e => onChange({ questionAsked: e.target.value })}
          placeholder='e.g. "Serve who?" or "What would you do in the first 90 days?"'
          rows={2}
          className={inputClass}
        />
      </label>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          What level was the question at?
        </span>
        <select
          value={scenario.questionLevel}
          onChange={e => onChange({ questionLevel: e.target.value as LogicalLevel | '' })}
          className={selectClass}
        >
          <option value="">Select level…</option>
          {LOGICAL_LEVELS.map(l => (
            <option key={l.id} value={l.id}>
              {l.label} — {l.questionWord}
            </option>
          ))}
        </select>
      </label>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          What was the response? (summary or quote)
        </span>
        <textarea
          value={scenario.responseGiven}
          onChange={e => onChange({ responseGiven: e.target.value })}
          placeholder="What they actually said — including any drift into identity or philosophy"
          rows={5}
          className={inputClass}
        />
      </label>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          What level did the response come from?
        </span>
        <select
          value={scenario.responseLevel}
          onChange={e => onChange({ responseLevel: e.target.value as LogicalLevel | '' })}
          className={selectClass}
        >
          <option value="">Select level…</option>
          {LOGICAL_LEVELS.map(l => (
            <option key={l.id} value={l.id}>
              {l.label} — {l.questionWord}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function DiagnoseStep({
  embedded,
  scenario,
  onToggleMarker,
  onChange,
  inputClass,
}: {
  embedded: boolean;
  scenario: LevelAwareState['scenario'];
  onToggleMarker: (m: LevelShiftMarker) => void;
  onChange: (updates: Partial<LevelAwareState['scenario']>) => void;
  inputClass: string;
}) {
  const suggested = useMemo(() => suggestMarkers(scenario), [scenario]);
  const coachSuggestion = useMemo(() => suggestCoachMove(scenario), [scenario]);

  return (
    <div className="space-y-6">
      <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
        Diagnose the shift
      </h2>

      <div
        className={` border p-4 grid sm:grid-cols-2 gap-3 text-sm ${
          embedded ? 'border-surface-border bg-surface-elevated/30' : 'border-charcoal-100 bg-[var(--surface)]'
        }`}
      >
        <div>
          <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            Question level
          </div>
          <div className={embedded ? 'text-text-primary' : 'text-charcoal-900'}>
            {levelLabel(scenario.questionLevel)}
          </div>
        </div>
        <div>
          <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            Response level
          </div>
          <div className={embedded ? 'text-text-primary' : 'text-charcoal-900'}>
            {levelLabel(scenario.responseLevel)}
          </div>
        </div>
      </div>

      <div>
        <p className={`text-sm mb-3 ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
          Which markers showed up? (We pre-selected likely ones — adjust as needed.)
        </p>
        <div className="space-y-2">
          {LEVEL_SHIFT_MARKERS.map(marker => {
            const active = scenario.markers.includes(marker.id);
            const suggestedHit = suggested.includes(marker.id);
            return (
              <button
                key={marker.id}
                type="button"
                onClick={() => onToggleMarker(marker.id)}
                className={`w-full text-left  border p-3 transition-colors ${
                  active
                    ? embedded
                      ? 'border-[var(--green)]/40 bg-[var(--green)]/10'
                      : 'border-[var(--green)] bg-[var(--green-dim)]'
                    : embedded
                      ? 'border-surface-border hover:border-[var(--green)]/20'
                      : 'border-charcoal-100 hover:border-charcoal-200'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-medium ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
                    {marker.label}
                  </span>
                  {suggestedHit && !active && (
                    <span className={`text-[10px] font-mono ${embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}`}>
                      suggested
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-1 ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
                  {marker.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {coachSuggestion && (
        <div
          className={` border p-4 text-sm ${
            embedded ? 'border-[var(--mustard)]/20 bg-[var(--mustard)]/5 text-text-secondary' : 'border-[var(--mustard)] bg-[var(--mustard-dim)]/60 text-charcoal-700'
          }`}
        >
          <span className="font-medium">Coach move:</span> {coachSuggestion}
        </div>
      )}

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          Your diagnosis notes (optional)
        </span>
        <textarea
          value={scenario.diagnosisNotes}
          onChange={e => onChange({ diagnosisNotes: e.target.value })}
          rows={3}
          className={inputClass}
          placeholder="What did you notice about the coherence breakdown?"
        />
      </label>
    </div>
  );
}

function ReanchorStep({
  embedded,
  scenario,
  onChange,
  inputClass,
}: {
  embedded: boolean;
  scenario: LevelAwareState['scenario'];
  onChange: (updates: Partial<LevelAwareState['scenario']>) => void;
  inputClass: string;
}) {
  return (
    <div className="space-y-6">
      <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
        Re-anchor the conversation
      </h2>

      <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        Name the level shift, restore the level of the original question, then rebuild. Use a script
        like these — or write your own.
      </p>

      <div className="space-y-3">
        {REANCHOR_SCRIPTS.map(script => (
          <button
            key={script.situation}
            type="button"
            onClick={() => onChange({ coachMove: script.script })}
            className={`w-full text-left  border p-3 text-sm transition-colors ${
              embedded
                ? 'border-surface-border hover:border-[var(--green)]/30 hover:bg-[var(--green)]/5'
                : 'border-charcoal-100 hover:border-consulting-slate-blue-200 hover:bg-consulting-slate-blue-50/40'
            }`}
          >
            <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
              {script.situation}
            </div>
            <p className={`italic ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
              &ldquo;{script.script}&rdquo;
            </p>
          </button>
        ))}
      </div>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          Your coach move (name the shift + return to question level)
        </span>
        <textarea
          value={scenario.coachMove}
          onChange={e => onChange({ coachMove: e.target.value })}
          rows={3}
          className={inputClass}
        />
      </label>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          Re-anchored answer — at the question&apos;s level
        </span>
        <textarea
          value={scenario.reanchoredResponse}
          onChange={e => onChange({ reanchoredResponse: e.target.value })}
          rows={4}
          className={inputClass}
          placeholder="Answer the original question at the level it lives — before going higher intentionally."
        />
      </label>
    </div>
  );
}

function ResultsStep({ embedded, state }: { embedded: boolean; state: LevelAwareState }) {
  const s = state.scenario;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Sparkles className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Your level-aware protocol
        </h2>
      </div>

      <div className="space-y-4">
        <ResultBlock embedded={embedded} title="Original question" body={s.questionAsked} />
        <ResultBlock
          embedded={embedded}
          title="Level map"
          body={`Question: ${levelLabel(s.questionLevel)} → Response: ${levelLabel(s.responseLevel)}`}
        />
        <ResultBlock
          embedded={embedded}
          title="Markers"
          body={s.markers.map(m => LEVEL_SHIFT_MARKERS.find(x => x.id === m)?.label).join(', ') || '—'}
        />
        {s.coachMove && <ResultBlock embedded={embedded} title="Coach move" body={`"${s.coachMove}"`} />}
        <ResultBlock embedded={embedded} title="Re-anchored answer" body={s.reanchoredResponse} />
      </div>

      <p className={`text-sm ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
        Repeat with new moments until naming the shift feels natural — then you can elevate the
        conversation on purpose instead of by accident.
      </p>
    </div>
  );
}

function ResultBlock({
  embedded,
  title,
  body,
}: {
  embedded: boolean;
  title: string;
  body: string;
}) {
  return (
    <div
      className={` border p-4 ${
        embedded ? 'border-[var(--green)]/20 bg-[var(--green)]/5' : 'border-consulting-slate-blue-200 bg-consulting-slate-blue-50/40'
      }`}
    >
      <div
        className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${
          embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'
        }`}
      >
        {title}
      </div>
      <p className={`text-sm whitespace-pre-wrap ${embedded ? 'text-text-secondary' : 'text-charcoal-800'}`}>
        {body || '—'}
      </p>
    </div>
  );
}
